import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FaceDetection, Results, Detection, ResultsListener } from "@mediapipe/face_detection";
import { FaceBlurResult, FaceBlurConfig, FaceBlurStatus, MediaPipeFaceDetection } from "../types/face-detection";
import { stackBlur } from "../stackBlur";

const useMediaPipeFaceDetection = (config?: Partial<FaceBlurConfig>) => {
  const [status, setStatus] = useState<FaceBlurStatus>("idle");
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const faceDetectionRef = useRef<FaceDetection | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isMountedRef = useRef(true);
  const isCleaningUpRef = useRef(false);

  const defaultConfig: FaceBlurConfig = useMemo(() => {
    return {
      modelType: "short_range",
      minDetectionConfidence: 0.3,
      blurRadius: 200,
      fallbackBlur: true,
      ellipseScaleX: 2.5,
      ellipseScaleY: 2,
      ...config,
    } as FaceBlurConfig;
  }, [config]);

  const initializeDetection = useCallback(async () => {
    if (isCleaningUpRef.current) {
      return;
    }

    try {
      setStatus("loading");
      setError(null);

      if (faceDetectionRef.current && !isCleaningUpRef.current) {
        try {
          await faceDetectionRef.current.close();
        } catch (error) {}
        faceDetectionRef.current = null;
        setIsInitialized(false);
      }

      if (isCleaningUpRef.current || !isMountedRef.current) {
        return;
      }

      const faceDetection = new FaceDetection({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`;
        },
      });
      faceDetection.setOptions({
        model: defaultConfig.modelType,
        minDetectionConfidence: defaultConfig.minDetectionConfidence,
      });

      const resultsHandler: ResultsListener = (results: Results) => {
        if (!isMountedRef.current || isCleaningUpRef.current) {
          return;
        }

        if (defaultConfig.progressCallback) {
          defaultConfig.progressCallback(100);
        }
      };

      faceDetection.onResults(resultsHandler);

      await faceDetection.initialize();

      if (isMountedRef.current && !isCleaningUpRef.current) {
        faceDetectionRef.current = faceDetection;
        setIsInitialized(true);
        setStatus("idle");
      } else {
        if (faceDetection) {
          try {
            await faceDetection.close();
          } catch (error) {}
        }
      }
    } catch (err) {
      if (isMountedRef.current && !isCleaningUpRef.current) {
        const errorMessage = err instanceof Error ? err.message : "MediaPipe initialization failed";

        setError(errorMessage);
        setStatus("error");
        if (defaultConfig.errorCallback) {
          defaultConfig.errorCallback(errorMessage);
        }
      }
    }
  }, [defaultConfig]);

  const reinitialize = useCallback(async () => {
    if (status === "loading") {
      return;
    }
    await initializeDetection();
  }, [status, initializeDetection]);

  useEffect(() => {
    isMountedRef.current = true;
    isCleaningUpRef.current = false;
    initializeDetection();
    return () => {
      isCleaningUpRef.current = true;
      isMountedRef.current = false;

      if (faceDetectionRef.current) {
        try {
          faceDetectionRef.current.close();
        } catch (error) {
        } finally {
          faceDetectionRef.current = null;
        }
      }
    };
  }, []);

  const transformDetection = useCallback((detection: Detection): MediaPipeFaceDetection => {
    return {
      boundingBox: {
        originX: detection.boundingBox.xCenter - detection.boundingBox.width / 2,
        originY: detection.boundingBox.yCenter - detection.boundingBox.height / 2,
        width: detection.boundingBox.width,
        height: detection.boundingBox.height,
      },
      landmarks: detection.landmarks || [],
      classifications: [],
    };
  }, []);

  const base64ToImage = useCallback((base64: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`;
    });
  }, []);

  const applyBlurToFaces = useCallback(
    (image: HTMLImageElement, detections: MediaPipeFaceDetection[]): string => {
      if (!canvasRef.current) {
        canvasRef.current = document.createElement("canvas");
      }

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d")!;

      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);

      detections.forEach((detection) => {
        const { boundingBox } = detection;
        const padding = 0.1;
        const x = Math.max(0, (boundingBox.originX - padding * boundingBox.width) * image.width);
        const y = Math.max(0, (boundingBox.originY - padding * boundingBox.height) * image.height);
        const width = Math.min(image.width - x, boundingBox.width * (1 + 2 * padding) * image.width);
        const height = Math.min(image.height - y, boundingBox.height * (1 + 2 * padding) * image.height);

        const tempCanvas = document.createElement("canvas");
        const tempCtx = tempCanvas.getContext("2d")!;

        tempCanvas.width = width;
        tempCanvas.height = height;
        tempCtx.drawImage(image, x, y, width, height, 0, 0, width, height);

        try {
          const imageData = tempCtx.getImageData(0, 0, width, height);

          const blurCanvas = document.createElement("canvas");
          blurCanvas.width = width;
          blurCanvas.height = height;

          stackBlur(imageData, blurCanvas, 25);

          ctx.save();
          ctx.beginPath();

          let radiusX: number;
          let radiusY: number;

          if (defaultConfig.ellipseRadius !== undefined && defaultConfig.ellipseRadius !== null) {
            radiusX = defaultConfig.ellipseRadius;
            radiusY = defaultConfig.ellipseRadius;
          } else {
            const scaleX = defaultConfig.ellipseScaleX ?? 2.5;
            const scaleY = defaultConfig.ellipseScaleY ?? 2;
            radiusX = width / scaleX;
            radiusY = height / scaleY;
          }

          ctx.ellipse(x + width / 2, y + height / 2, radiusX, radiusY, 0, 0, Math.PI * 2);
          ctx.clip();

          ctx.drawImage(blurCanvas, 0, 0, width, height, x, y, width, height);

          ctx.restore();
        } catch (error) {
          const blurPasses = 5;
          const scaleFactor = 0.08;

          for (let i = 0; i < blurPasses; i++) {
            const smallWidth = Math.max(2, width * scaleFactor);
            const smallHeight = Math.max(2, height * scaleFactor);

            const smallCanvas = document.createElement("canvas");
            const smallCtx = smallCanvas.getContext("2d")!;
            smallCanvas.width = smallWidth;
            smallCanvas.height = smallHeight;

            smallCtx.imageSmoothingEnabled = true;
            smallCtx.imageSmoothingQuality = "high";
            smallCtx.drawImage(tempCanvas, 0, 0, smallWidth, smallHeight);

            tempCtx.clearRect(0, 0, width, height);
            tempCtx.imageSmoothingEnabled = true;
            tempCtx.imageSmoothingQuality = "high";
            tempCtx.drawImage(smallCanvas, 0, 0, width, height);
          }

          ctx.drawImage(tempCanvas, 0, 0, width, height, x, y, width, height);
        }
      });

      const dataURL = canvas.toDataURL("image/jpeg", 0.9);

      return dataURL.split(",")[1];
    },
    [defaultConfig]
  );

  const applyFallbackBlur = useCallback((image: HTMLImageElement): string => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;

    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    const blurHeight = image.height * 0.6;

    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d")!;
    tempCanvas.width = image.width;
    tempCanvas.height = blurHeight;

    tempCtx.drawImage(image, 0, 0, image.width, blurHeight, 0, 0, image.width, blurHeight);

    const blurPasses = 5;
    const scaleFactor = 0.08;

    for (let i = 0; i < blurPasses; i++) {
      const smallWidth = Math.max(2, image.width * scaleFactor);
      const smallHeight = Math.max(2, blurHeight * scaleFactor);

      const smallCanvas = document.createElement("canvas");
      const smallCtx = smallCanvas.getContext("2d")!;
      smallCanvas.width = smallWidth;
      smallCanvas.height = smallHeight;

      smallCtx.imageSmoothingEnabled = true;
      smallCtx.imageSmoothingQuality = "high";
      smallCtx.drawImage(tempCanvas, 0, 0, smallWidth, smallHeight);

      tempCtx.clearRect(0, 0, image.width, blurHeight);
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = "high";
      tempCtx.drawImage(smallCanvas, 0, 0, image.width, blurHeight);
    }

    ctx.drawImage(tempCanvas, 0, 0, image.width, blurHeight, 0, 0, image.width, blurHeight);

    const dataURL = canvas.toDataURL("image/jpeg", 0.9);

    return dataURL.split(",")[1];
  }, []);

  const blurFaces = useCallback(
    async (base64Image: string): Promise<FaceBlurResult> => {
      const startTime = performance.now();

      try {
        if (!isInitialized || !faceDetectionRef.current || isCleaningUpRef.current) {
          throw new Error("MediaPipe not initialized");
        }

        const currentInstance = faceDetectionRef.current;

        setStatus("detecting");

        if (defaultConfig.progressCallback) {
          defaultConfig.progressCallback(10);
        }

        const image = await base64ToImage(base64Image);

        if (defaultConfig.progressCallback) {
          defaultConfig.progressCallback(30);
        }

        const detections: MediaPipeFaceDetection[] = [];
        let resultsReceived = false;

        const resultsHandler = (results: Results) => {
          detections.length = 0;

          if (results.detections) {
            results.detections.forEach((detection) => {
              detections.push(transformDetection(detection));
            });
          }
          resultsReceived = true;
        };

        const originalHandler = currentInstance.onResults.bind(currentInstance);
        currentInstance.onResults(resultsHandler);

        await currentInstance.send({ image });

        let waitTime = 0;
        const maxWaitTime = 5000;
        while (!resultsReceived && waitTime < maxWaitTime && !isCleaningUpRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 50));
          waitTime += 50;
        }

        if (currentInstance && !isCleaningUpRef.current) {
          currentInstance.onResults(originalHandler);
        }

        if (defaultConfig.progressCallback) {
          defaultConfig.progressCallback(70);
        }

        setStatus("blurring");

        let blurredImage: string;
        let blurredFacesCount = detections.length;

        if (detections.length > 0) {
          blurredImage = applyBlurToFaces(image, detections);
        } else if (defaultConfig.fallbackBlur) {
          blurredImage = applyFallbackBlur(image);
          blurredFacesCount = 1;
        } else {
          blurredImage = base64Image;
        }

        if (defaultConfig.progressCallback) {
          defaultConfig.progressCallback(100);
        }

        const processingTime = performance.now() - startTime;

        setStatus("success");

        return {
          success: true,
          blurredImage,
          originalFacesCount: detections.length,
          blurredFacesCount,
          processingTime,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Face blur processing failed";

        setError(errorMessage);
        setStatus("error");

        if (defaultConfig.errorCallback) {
          defaultConfig.errorCallback(errorMessage);
        }

        const processingTime = performance.now() - startTime;
        return {
          success: false,
          blurredImage: base64Image,
          originalFacesCount: 0,
          blurredFacesCount: 0,
          processingTime,
          error: errorMessage,
        };
      }
    },
    [isInitialized, defaultConfig, base64ToImage, transformDetection, applyBlurToFaces, applyFallbackBlur]
  );

  return {
    blurFaces,
    status,
    isInitialized,
    error,
    reinitialize,
  };
};

export default useMediaPipeFaceDetection;
